import { useMemo } from 'react';
import { create } from 'qrcode';
import './DesenhoDeQr.css';

/** Zona de silêncio exigida pela norma do QR: 4 módulos de cada lado. */
const MARGEM = 4;

type Props = {
  readonly conteudo: string;
  /** Nome do desenho para o leitor de tela. */
  readonly descricao: string;
  /** Texto do aviso quando o conteúdo não cabe em um QR. */
  readonly mensagemDeFalha: string;
  readonly classe?: string;
};

/**
 * Desenha o QR a partir dos módulos, como um caminho SVG único.
 *
 * Devolve `null` quando o conteúdo não cabe em um QR — texto longo demais
 * estoura a capacidade da maior versão, e a tela precisa dizer isso em vez de
 * quebrar.
 */
function caminhoDoQr(conteudo: string): { readonly d: string; readonly lado: number } | null {
  try {
    const { modules } = create(conteudo, { errorCorrectionLevel: 'M' });
    const lado = modules.size;
    const partes: string[] = [];

    for (let linha = 0; linha < lado; linha += 1) {
      for (let coluna = 0; coluna < lado; coluna += 1) {
        if (modules.data[linha * lado + coluna]) {
          partes.push(`M${String(coluna)} ${String(linha)}h1v1h-1z`);
        }
      }
    }

    return { d: partes.join(''), lado };
  } catch {
    return null;
  }
}

/**
 * O desenho do QR, e só ele.
 *
 * Nasceu dentro do bloco de pareamento e saiu de lá quando o gerador de QR de
 * pedido apareceu: são dois conteúdos com textos e validades diferentes, mas o
 * desenho é o mesmo, e duplicá-lo significaria manter duas vezes a decisão de
 * gerar LOCALMENTE (RNF-026) — mandar um código de pareamento ou um pedido
 * inteiro para um serviço de imagem de terceiros seria entregar dado do
 * cliente de bandeja.
 *
 * O SVG usa `currentColor`: a cor sai do token da folha de estilo, e o QR
 * acompanha o tema sem nenhum valor de cor escrito no componente.
 */
export function DesenhoDeQr({ conteudo, descricao, mensagemDeFalha, classe }: Props) {
  const desenho = useMemo(() => caminhoDoQr(conteudo), [conteudo]);

  if (desenho === null) {
    return (
      <p className="desenho-qr__falha" role="alert">
        {mensagemDeFalha}
      </p>
    );
  }

  return (
    <svg
      className={classe ?? 'desenho-qr'}
      role="img"
      aria-label={descricao}
      viewBox={`${String(-MARGEM)} ${String(-MARGEM)} ${String(desenho.lado + MARGEM * 2)} ${String(desenho.lado + MARGEM * 2)}`}
      shapeRendering="crispEdges"
    >
      <path d={desenho.d} fill="currentColor" />
    </svg>
  );
}
